import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowUp,
  Square,
  Paperclip,
  X,
  FileText,
  Globe,
  Sparkles,
  Search,
  ChevronDown,
} from 'lucide-react';

import { useChatStore } from '../store/chatStore';
import { useThemeStore } from '../store/themeStore';
import { ChatMode, ChatLang } from '../types';

// ─── Mode config — updated description for Normal ───
const MODE_CONFIG: Record<
  ChatMode,
  { label: string; icon: React.ReactNode; color: string; description: string }
> = {
  normal: {
    label: 'Normal',
    icon: <Sparkles size={15} />,
    color: 'text-emerald-400',
    description: 'Smart AI',
  },
  research: {
    label: 'Research',
    icon: <Search size={15} />,
    color: 'text-blue-400',
    description: 'Always searches web + AI analysis',
  },
  deep: {
    label: 'Deep Research',
    icon: <Globe size={15} />,
    color: 'text-purple-400',
    description: 'Multi-source deep analysis report',
  },
};

// ─── Language config ───
const LANG_CONFIG: Record<ChatLang, { label: string; flag: string; description: string }> = {
  auto: { label: 'Auto', flag: '🌐', description: 'Auto-detect language' },
  vi: { label: 'Tiếng Việt', flag: '🇻🇳', description: 'Trả lời bằng tiếng Việt' },
  en: { label: 'English', flag: '🇺🇸', description: 'Reply in English' },
};

const ALLOWED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const MAX_FILES = 5;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const ChatInput: React.FC = () => {
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previews, setPreviews] = useState<{ file: File; url?: string }[]>([]);
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modeMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);

  const sendChatMessage = useChatStore((s) => s.sendChatMessage);
  const stopGenerating = useChatStore((s) => s.stopGenerating);
  const isGenerating = useChatStore((s) => s.isGenerating);
  const currentMode = useChatStore((s) => s.currentMode);
  const setCurrentMode = useChatStore((s) => s.setCurrentMode);
  const currentLang = useChatStore((s) => s.currentLang);
  const setCurrentLang = useChatStore((s) => s.setCurrentLang);
  const theme = useThemeStore((s) => s.theme);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => { adjustTextareaHeight(); }, [input, adjustTextareaHeight]);
  useEffect(() => { textareaRef.current?.focus(); }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (modeMenuRef.current && !modeMenuRef.current.contains(e.target as Node)) setShowModeMenu(false);
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) setShowLangMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    return () => { previews.forEach((p) => p.url && URL.revokeObjectURL(p.url)); };
  }, [previews]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFileError(null);
    if (attachments.length + files.length > MAX_FILES) { setFileError(`Maximum ${MAX_FILES} files allowed.`); return; }
    const valid: File[] = [];
    for (const file of files) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) { setFileError(`Unsupported: ${file.name}.`); return; }
      if (file.size > MAX_FILE_SIZE) { setFileError(`Too large: ${file.name}. Max 20MB.`); return; }
      valid.push(file);
    }
    const newPreviews = valid.map((f) => ({ file: f, url: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined }));
    setAttachments((prev) => [...prev, ...valid]);
    setPreviews((prev) => [...prev, ...newPreviews]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index: number) => {
    const p = previews[index];
    if (p?.url) URL.revokeObjectURL(p.url);
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  };

  const handleSubmit = useCallback(async () => {
    const trimmed = input.trim();
    if ((!trimmed && attachments.length === 0) || isGenerating) return;
    const filesToSend = [...attachments];
    setInput(''); setAttachments([]); setPreviews([]); setFileError(null);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    await sendChatMessage(trimmed || '(Attached files)', filesToSend.length > 0 ? filesToSend : undefined);
    textareaRef.current?.focus();
  }, [input, attachments, isGenerating, sendChatMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
  }, [handleSubmit]);

  const canSend = (input.trim().length > 0 || attachments.length > 0) && !isGenerating;
  const modeConfig = MODE_CONFIG[currentMode];
  const langConfig = LANG_CONFIG[currentLang];

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 pb-4 pt-2">
      {/* File Error */}
      <AnimatePresence>
        {fileError && (
          <motion.div initial={{ opacity: 0, y: 10, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }} exit={{ opacity: 0, y: 10, height: 0 }}
            className="mb-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-center justify-between">
            <span>{fileError}</span>
            <button onClick={() => setFileError(null)} className="ml-2 hover:text-red-300"><X size={14} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Attachment Previews */}
      <AnimatePresence>
        {previews.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-2 flex flex-wrap gap-2">
            {previews.map((preview, index) => (
              <motion.div key={`${preview.file.name}-${index}`} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                className={`relative group rounded-xl overflow-hidden border ${theme === 'dark' ? 'border-gpt-dark-border bg-gpt-dark-input' : 'border-gpt-light-border bg-gpt-light-input'}`}>
                {preview.url ? (
                  <div className="w-20 h-20 relative">
                    <img src={preview.url} alt={preview.file.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                  </div>
                ) : (
                  <div className="w-44 h-16 flex items-center gap-2 px-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-gpt-dark-hover' : 'bg-gpt-light-hover'}`}>
                      <FileText size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs truncate font-medium ${theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'}`}>{preview.file.name}</p>
                      <p className={`text-[10px] ${theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'}`}>{formatFileSize(preview.file.size)}</p>
                    </div>
                  </div>
                )}
                <button onClick={() => removeAttachment(index)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10">
                  <X size={10} />
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Input */}
      <div className={`relative flex flex-col rounded-2xl border transition-colors ${theme === 'dark' ? 'bg-gpt-dark-input border-gpt-dark-border/50 focus-within:border-gpt-dark-border' : 'bg-gpt-light-input border-gpt-light-border focus-within:border-gray-400'}`}>
        {/* Top Row */}
        <div className="flex items-center gap-1 px-2 pt-2">
          {/* Mode Selector */}
          <div className="relative" ref={modeMenuRef}>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => { setShowModeMenu(!showModeMenu); setShowLangMenu(false); }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${theme === 'dark' ? 'hover:bg-gpt-dark-hover' : 'hover:bg-gpt-light-hover'} ${modeConfig.color}`}>
              {modeConfig.icon}
              <span>{modeConfig.label}</span>
              <ChevronDown size={12} className={`transition-transform ${showModeMenu ? 'rotate-180' : ''}`} />
            </motion.button>
            <AnimatePresence>
              {showModeMenu && (
                <motion.div initial={{ opacity: 0, y: 5, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 5, scale: 0.95 }} transition={{ duration: 0.15 }}
                  className={`absolute bottom-full left-0 mb-2 w-64 rounded-xl shadow-xl border overflow-hidden z-50 ${theme === 'dark' ? 'bg-gpt-dark-sidebar border-gpt-dark-border' : 'bg-white border-gpt-light-border'}`}>
                  <div className="p-1.5">
                    {(Object.entries(MODE_CONFIG) as [ChatMode, (typeof MODE_CONFIG)['normal']][]).map(([mode, config]) => (
                      <motion.button key={mode} whileHover={{ x: 2 }}
                        onClick={() => { setCurrentMode(mode); setShowModeMenu(false); }}
                        className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${currentMode === mode ? (theme === 'dark' ? 'bg-gpt-dark-hover' : 'bg-gpt-light-hover') : (theme === 'dark' ? 'hover:bg-gpt-dark-hover/50' : 'hover:bg-gpt-light-hover/50')}`}>
                        <span className={`mt-0.5 ${config.color}`}>{config.icon}</span>
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'}`}>{config.label}</p>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'}`}>{config.description}</p>
                        </div>
                        {currentMode === mode && <motion.div layoutId="mode-indicator" className={`ml-auto mt-1 w-1.5 h-1.5 rounded-full ${config.color.replace('text-', 'bg-')}`} />}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Lang Selector */}
          <div className="relative" ref={langMenuRef}>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => { setShowLangMenu(!showLangMenu); setShowModeMenu(false); }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${theme === 'dark' ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary' : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'}`}>
              <span className="text-sm">{langConfig.flag}</span>
              <span>{langConfig.label}</span>
              <ChevronDown size={12} className={`transition-transform ${showLangMenu ? 'rotate-180' : ''}`} />
            </motion.button>
            <AnimatePresence>
              {showLangMenu && (
                <motion.div initial={{ opacity: 0, y: 5, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 5, scale: 0.95 }} transition={{ duration: 0.15 }}
                  className={`absolute bottom-full left-0 mb-2 w-52 rounded-xl shadow-xl border overflow-hidden z-50 ${theme === 'dark' ? 'bg-gpt-dark-sidebar border-gpt-dark-border' : 'bg-white border-gpt-light-border'}`}>
                  <div className="p-1.5">
                    {(Object.entries(LANG_CONFIG) as [ChatLang, (typeof LANG_CONFIG)['auto']][]).map(([lang, config]) => (
                      <motion.button key={lang} whileHover={{ x: 2 }}
                        onClick={() => { setCurrentLang(lang); setShowLangMenu(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${currentLang === lang ? (theme === 'dark' ? 'bg-gpt-dark-hover' : 'bg-gpt-light-hover') : (theme === 'dark' ? 'hover:bg-gpt-dark-hover/50' : 'hover:bg-gpt-light-hover/50')}`}>
                        <span className="text-lg">{config.flag}</span>
                        <div>
                          <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'}`}>{config.label}</p>
                          <p className={`text-[11px] ${theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'}`}>{config.description}</p>
                        </div>
                        {currentLang === lang && <motion.div layoutId="lang-indicator" className="ml-auto w-1.5 h-1.5 rounded-full bg-gpt-green" />}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className={`w-px h-4 mx-0.5 ${theme === 'dark' ? 'bg-gpt-dark-border/40' : 'bg-gpt-light-border'}`} />

          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => fileInputRef.current?.click()} disabled={isGenerating}
            className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary' : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'} ${isGenerating ? 'opacity-40 cursor-not-allowed' : ''}`}
            title="Attach files">
            <Paperclip size={16} />
          </motion.button>

          <input ref={fileInputRef} type="file" multiple accept={ALLOWED_FILE_TYPES.join(',')} onChange={handleFileSelect} className="hidden" />
        </div>

        {/* Textarea */}
        <textarea ref={textareaRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
          placeholder={currentMode === 'deep' ? 'Ask for deep research analysis...' : currentMode === 'research' ? 'Ask with web sources...' : 'Message VNGPT...'}
          rows={1} disabled={isGenerating}
          className={`flex-1 resize-none bg-transparent py-3 pl-4 pr-12 text-[15px] leading-6 outline-none max-h-[200px] ${theme === 'dark' ? 'text-gpt-dark-text placeholder:text-gpt-dark-text-secondary/70' : 'text-gpt-light-text placeholder:text-gpt-light-text-secondary'} ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ minHeight: '44px' }} aria-label="Message input" />

        {/* Send/Stop */}
        <div className="absolute right-2 bottom-2">
          {isGenerating ? (
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={stopGenerating}
              className={`p-1.5 rounded-lg transition-all duration-200 ${theme === 'dark' ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}`} aria-label="Stop">
              <Square size={18} />
            </motion.button>
          ) : (
            <motion.button whileHover={canSend ? { scale: 1.05 } : {}} whileTap={canSend ? { scale: 0.95 } : {}} onClick={handleSubmit} disabled={!canSend}
              className={`p-1.5 rounded-lg transition-all duration-200 ${canSend ? (theme === 'dark' ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800') : (theme === 'dark' ? 'bg-gpt-dark-border/50 text-gpt-dark-text-secondary/50' : 'bg-gray-300 text-gray-500')}`} aria-label="Send">
              <ArrowUp size={18} />
            </motion.button>
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-between mt-2 px-1">
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1 text-xs ${modeConfig.color}`}>
            {modeConfig.icon}
            <span className="opacity-70">{modeConfig.label}</span>
          </div>
          <div className={`flex items-center gap-1 text-xs ${theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'}`}>
            <span className="text-xs">{langConfig.flag}</span>
            <span className="opacity-70">{langConfig.label}</span>
          </div>
        </div>
        <p className={`text-xs ${theme === 'dark' ? 'text-gpt-dark-text-secondary/60' : 'text-gpt-light-text-secondary'}`}>
          VNGPT can make mistakes. Verify important info.
        </p>
      </div>
    </div>
  );
};

export default ChatInput;